<template>
	<el-container class="pricing-page">
		<el-main class="pricing-content">
			<!-- Header Section -->
			<div class="pricing-header">
				<h1 class="pricing-title">智能体分档付费说明</h1>
				<p class="pricing-subtitle">选择适合您的计费周期，畅享智能体服务</p>
			</div>

			<!-- Billing Tiers Cards -->
			<div class="tiers-grid">
				<el-card class="tier-card" v-for="tier in billingTiers" :key="tier.id" shadow="hover">
					<div class="tier-header">
						<i :class="tier.icon" class="tier-icon"></i>
						<h3 class="tier-name">{{ tier.name }}</h3>
					</div>
					<div class="tier-duration">{{ tier.duration }}</div>
					<div class="tier-price-range">
						<span class="tier-min-price">¥{{ tier.minPrice }}</span>
						<span class="tier-separator">-</span>
						<span class="tier-max-price">¥{{ tier.maxPrice }}</span>
					</div>
					<div v-if="tier.discount" class="tier-discount">
						<el-tag type="success" size="small">{{ tier.discount }}</el-tag>
					</div>
					<div class="tier-description">{{ tier.description }}</div>
				</el-card>
			</div>

			<!-- Cost Breakdown Section -->
			<div class="cost-section">
				<h2 class="section-title">费用构成说明</h2>
				<div class="cost-cards">
					<el-card class="cost-card" shadow="hover">
						<div class="cost-card-header">
							<i class="el-icon-collection-tag cost-icon subscription-icon"></i>
							<h3 class="cost-name">智能体订阅费</h3>
						</div>
						<p class="cost-description">
							这是使用智能体服务的基础费用，根据您选择的计费周期（按天/月/季/年）进行收费。订阅期间可以无限次启动对话，创建会话。
						</p>
						<ul class="cost-features">
							<li>✓ 订阅期内无限制使用</li>
							<li>✓ 享受创作者设定的所有能力</li>
							<li>✓ 访问智能体的技能和工作流</li>
						</ul>
					</el-card>

					<el-card class="cost-card" shadow="hover">
						<div class="cost-card-header">
							<i class="el-icon-lightning cost-icon token-icon"></i>
							<h3 class="cost-name">Token 消耗费</h3>
						</div>
						<p class="cost-description">
							这是大模型实际处理您对话时产生的费用，按照实际使用的 Token 数量计费。不同的大模型价格不同，会话越长消耗越多。
						</p>
						<ul class="cost-features">
							<li>✓ 按实际使用量计费</li>
							<li>✓ 透明的消费记录</li>
							<li>✓ 支持多种大模型</li>
						</ul>
					</el-card>
				</div>
				<el-alert
					type="info"
					:closable="false"
					class="cost-alert"
					title="重要提示"
					description="使用智能体服务需要同时支付订阅费和Token消耗费。订阅费确保您能使用智能体，Token费根据实际对话量收费。">
				</el-alert>
			</div>

			<!-- FAQ Section -->
			<div class="faq-section">
				<h2 class="section-title">常见问题</h2>
				<el-collapse accordion>
					<el-collapse-item title="如何选择合适的计费周期？" name="1">
						<p>建议根据您的使用频率选择：</p>
						<ul>
							<li><strong>按天订阅：</strong>适合偶尔使用或试用的场景</li>
							<li><strong>按月订阅：</strong>适合日常使用的个人用户</li>
							<li><strong>按季订阅：</strong>享受 20% 优惠，适合长期使用的用户</li>
							<li><strong>按年订阅：</strong>享受 30% 优惠，最适合团队或重度使用者</li>
						</ul>
					</el-collapse-item>

					<el-collapse-item title="订阅费和 Token 费有什么区别？" name="2">
						<p>
							<strong>订阅费：</strong>是使用智能体的"门票"，按周期一次性支付，让您能够访问和使用该智能体的所有功能。
						</p>
						<p>
							<strong>Token 费：</strong>是使用大模型的"油费"，根据您实际对话产生的内容长度和使用的模型类型按量计费。
						</p>
						<p class="tip-text">
							💡 可以理解为：订阅费是租车的费用，Token费是加油的费用。
						</p>
					</el-collapse-item>

					<el-collapse-item title="订阅到期后会发生什么？" name="3">
						<p>订阅到期后：</p>
						<ul>
							<li>无法继续使用该智能体创建新的会话</li>
							<li>已有的会话历史记录仍然保留</li>
							<li>可以随时续订恢复使用</li>
							<li>续订后立即恢复所有功能</li>
						</ul>
					</el-collapse-item>

					<el-collapse-item title="如何控制 Token 消耗？" name="4">
						<p>以下方法可以帮助您节省 Token 费用：</p>
						<ul>
							<li>简洁明确的提问可以减少不必要的对话轮次</li>
							<li>选择性价比更高的模型（如 GPT-3.5 而非 GPT-4）</li>
							<li>避免重复询问相同的问题</li>
							<li>定期清理不需要的会话历史</li>
						</ul>
					</el-collapse-item>

					<el-collapse-item title="可以只购买订阅不充值 Token 吗？" name="5">
						<p>
							可以购买订阅，但实际使用时必须有足够的 Token 余额。建议在订阅智能体前先充值适量的 Token，以确保顺畅使用。
						</p>
						<p>
							系统会实时显示您的 Token 余额，余额不足时会提醒您充值。
						</p>
					</el-collapse-item>

					<el-collapse-item title="订阅后可以退款吗？" name="6">
						<p>
							订阅费一经购买，原则上不予退款。但在以下特殊情况下可以申请退款：
						</p>
						<ul>
							<li>智能体存在严重功能缺陷且创作者未及时修复</li>
							<li>智能体描述与实际功能严重不符</li>
							<li>误操作购买（需在24小时内申请且未使用）</li>
						</ul>
						<p>
							如需申请退款，请联系客服并说明具体原因。
						</p>
					</el-collapse-item>
				</el-collapse>
			</div>

			<!-- Token Packages Section -->
			<div class="token-section">
				<h2 class="section-title">Token 充值套餐</h2>
				<div class="token-packages">
					<el-card class="token-package" v-for="pkg in tokenPackages" :key="pkg.id" shadow="hover"
						:class="{ 'recommended': pkg.isRecommended }">
						<div v-if="pkg.isRecommended" class="recommended-badge">推荐</div>
						<h3 class="package-name">{{ pkg.name }}</h3>
						<div class="package-amount">{{ formatTokenAmount(pkg.amount) }}</div>
						<div v-if="pkg.bonus" class="package-bonus">
							<el-tag type="warning" size="small">赠送 {{ formatTokenAmount(pkg.bonus) }}</el-tag>
						</div>
						<div class="package-price">¥{{ pkg.price }}</div>
					</el-card>
				</div>
			</div>
		</el-main>
	</el-container>
</template>

<script>
export default {
	name: 'HelpPricing',
	data() {
		return {
			billingTiers: [
				{
					id: 'daily',
					name: '按天计费',
					icon: 'el-icon-sunny',
					duration: '24小时',
					minPrice: 0.5,
					maxPrice: 50,
					discount: null,
					description: '短期使用，灵活便捷'
				},
				{
					id: 'monthly',
					name: '按月计费',
					icon: 'el-icon-date',
					duration: '30天',
					minPrice: 1.0,
					maxPrice: 500,
					discount: '优惠 10%',
					description: '日常使用的最佳选择'
				},
				{
					id: 'quarterly',
					name: '按季计费',
					icon: 'el-icon-data-analysis',
					duration: '90天',
					minPrice: 3.0,
					maxPrice: 1000,
					discount: '优惠 20%',
					description: '长期使用，性价比高'
				},
				{
					id: 'yearly',
					name: '按年计费',
					icon: 'el-icon-trophy',
					duration: '365天',
					minPrice: 10.0,
					maxPrice: 5000,
					discount: '优惠 30%',
					description: '最超值的选择'
				}
			],
			tokenPackages: [
				{ id: 'small', name: '入门包', amount: 100000, price: 9.9 },
				{ id: 'medium', name: '标准包', amount: 500000, price: 39.9, bonus: 50000 },
				{ id: 'large', name: '超值包', amount: 1000000, price: 69.9, bonus: 200000, isRecommended: true },
				{ id: 'xlarge', name: '企业包', amount: 5000000, price: 299.9, bonus: 1500000 }
			]
		};
	},
	methods: {
		formatTokenAmount(amount) {
			if (amount >= 1000000) {
				return (amount / 1000000).toFixed(1) + 'M';
			} else if (amount >= 1000) {
				return (amount / 1000).toFixed(0) + 'K';
			}
			return amount.toString();
		}
	}
};
</script>

<style lang="scss" scoped>
.pricing-page {
	background: #f5f7fa;
	min-height: 100vh;

	.pricing-content {
		max-width: 1200px;
		margin: 0 auto;
		padding: 40px 20px;
	}

	// Header Section
	.pricing-header {
		text-align: center;
		margin-bottom: 60px;

		.pricing-title {
			font-size: 36px;
			font-weight: 700;
			color: #2c3e50;
			margin: 0 0 16px 0;
		}

		.pricing-subtitle {
			font-size: 18px;
			color: #606266;
			margin: 0;
		}
	}

	// Billing Tiers Grid
	.tiers-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
		gap: 24px;
		margin-bottom: 60px;

		.tier-card {
			text-align: center;
			transition: transform 0.2s;

			&:hover {
				transform: translateY(-4px);
			}

			.tier-header {
				display: flex;
				flex-direction: column;
				align-items: center;
				margin-bottom: 16px;

				.tier-icon {
					font-size: 48px;
					color: #7C4DFF;
					margin-bottom: 12px;
				}

				.tier-name {
					font-size: 20px;
					font-weight: 600;
					color: #2c3e50;
					margin: 0;
				}
			}

			.tier-duration {
				font-size: 14px;
				color: #909399;
				margin-bottom: 16px;
			}

			.tier-price-range {
				display: flex;
				justify-content: center;
				align-items: baseline;
				margin-bottom: 12px;

				.tier-min-price {
					font-size: 24px;
					font-weight: 700;
					color: #7C4DFF;
				}

				.tier-separator {
					margin: 0 8px;
					color: #909399;
				}

				.tier-max-price {
					font-size: 24px;
					font-weight: 700;
					color: #7C4DFF;
				}
			}

			.tier-discount {
				margin-bottom: 12px;
			}

			.tier-description {
				font-size: 14px;
				color: #606266;
			}
		}
	}

	// Cost Section
	.cost-section {
		margin-bottom: 60px;

		.section-title {
			font-size: 28px;
			font-weight: 600;
			color: #2c3e50;
			text-align: center;
			margin: 0 0 32px 0;
		}

		.cost-cards {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
			gap: 24px;
			margin-bottom: 24px;

			.cost-card {
				.cost-card-header {
					display: flex;
					align-items: center;
					margin-bottom: 16px;

					.cost-icon {
						font-size: 28px;
						margin-right: 12px;

						&.subscription-icon {
							color: #7C4DFF;
						}

						&.token-icon {
							color: #FF9800;
						}
					}

					.cost-name {
						font-size: 20px;
						font-weight: 600;
						color: #2c3e50;
						margin: 0;
					}
				}

				.cost-description {
					font-size: 14px;
					color: #606266;
					line-height: 1.6;
					margin: 0 0 16px 0;
				}

				.cost-features {
					list-style: none;
					padding: 0;
					margin: 0;

					li {
						font-size: 14px;
						color: #606266;
						line-height: 2;

						&::before {
							content: '';
							display: inline-block;
							width: 4px;
							height: 4px;
							background: #7C4DFF;
							border-radius: 50%;
							margin-right: 8px;
						}
					}
				}
			}
		}

		.cost-alert {
			margin-top: 24px;
		}
	}

	// FAQ Section
	.faq-section {
		margin-bottom: 60px;

		.section-title {
			font-size: 28px;
			font-weight: 600;
			color: #2c3e50;
			text-align: center;
			margin: 0 0 32px 0;
		}

		p {
			margin: 0 0 12px 0;
			line-height: 1.6;
		}

		ul {
			margin: 0 0 12px 0;
			padding-left: 24px;

			li {
				margin: 8px 0;
				line-height: 1.6;
			}
		}

		.tip-text {
			background: #f0f9ff;
			border-left: 4px solid #7C4DFF;
			padding: 12px 16px;
			margin: 12px 0;
			border-radius: 4px;
		}
	}

	// Token Packages Section
	.token-section {
		.section-title {
			font-size: 28px;
			font-weight: 600;
			color: #2c3e50;
			text-align: center;
			margin: 0 0 32px 0;
		}

		.token-packages {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
			gap: 20px;

			.token-package {
				text-align: center;
				position: relative;
				transition: transform 0.2s;

				&:hover {
					transform: translateY(-4px);
				}

				&.recommended {
					border: 2px solid #7C4DFF;

					.recommended-badge {
						position: absolute;
						top: -10px;
						right: -10px;
						background: #7C4DFF;
						color: white;
						padding: 4px 12px;
						border-radius: 12px;
						font-size: 12px;
						font-weight: 600;
					}
				}

				.package-name {
					font-size: 18px;
					font-weight: 600;
					color: #2c3e50;
					margin: 0 0 12px 0;
				}

				.package-amount {
					font-size: 24px;
					font-weight: 700;
					color: #7C4DFF;
					margin-bottom: 8px;
				}

				.package-bonus {
					margin-bottom: 12px;
				}

				.package-price {
					font-size: 28px;
					font-weight: 700;
					color: #FF9800;
				}
			}
		}
	}
}
</style>
